import { Injectable } from '@angular/core';
import { combineLatest, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import {
    selectQuoteControllerState,
    selectWidgetByName,
} from 'sharedStores/quote-controller/quote-controller.selectors';
import { CmsSelectors } from 'sharedStores/cms/cms.module';
import { Page } from 'dataModels/page';
import { QuoteFlowType } from 'sharedEnums/quote-flow-type';
import { selectQuoteFlowType } from 'stores/quote/quote.selectors';
import { Section } from 'dataModels/section';
import { QuoteControllerState } from 'sharedStores/quote-controller/quote-controller.reducer';

export class TrackBarData {
    group: string;
    index: number;
    baseRoutes: string[];
    routes: string[];
    name: string;
    icon: string;
    quoteFlowType?: QuoteFlowType;
}

@Injectable({
    providedIn: 'root',
})
export class TrackbarService {
    constructor(private store$: Store) {}

    getTrackbarData(): Observable<{
        tracks: TrackBarData[];
        currentTrack: TrackBarData;
        percentage: string;
    }> {
        const quoteControllerState$ = this.store$.select(selectQuoteControllerState);
        const quoteSections$ = this.store$.select(CmsSelectors.selectSections);
        const trackBarWidget$ = this.store$.select(selectWidgetByName('track-bar-widget'));
        const quoteFlowType$ = this.store$.select(selectQuoteFlowType);

        return combineLatest([
            quoteControllerState$,
            quoteSections$,
            quoteFlowType$,
            trackBarWidget$,
        ]).pipe(
            map(([quoteControllerState, flowSections, quoteFlowType, trackBarWidget]) => {
                if (flowSections) {
                    const currentSection =
                        flowSections[quoteControllerState?.activeIndex?.sectionIndex];

                    let percentage = '0';

                    const trackBarData: TrackBarData[] =
                        trackBarWidget?.formObject?.data?.additionalData;

                    let tracks = trackBarData;
                    const currentTrack = this.getCurrentTrack(
                        tracks,
                        currentSection,
                        quoteFlowType,
                    );

                    tracks = tracks?.filter(
                        (el) =>
                            el.group === currentTrack?.group &&
                            (el.quoteFlowType == quoteFlowType || el.quoteFlowType == null),
                    );

                    const includedSections = this.getSections(flowSections, currentTrack);

                    const includedPages = this.getPages(currentTrack, includedSections);

                    const currentPageIndex = this.getCurrentPageIndex(
                        includedPages,
                        quoteControllerState,
                    );

                    percentage = this.calculateTrackbarPercentage(
                        currentPageIndex,
                        includedPages,
                        percentage,
                    );

                    return { tracks, percentage, currentTrack };
                }
                return {
                    tracks: [],
                    currentTrack: {
                        index: -1,
                        group: null,
                        baseRoutes: null,
                        routes: null,
                        name: null,
                        icon: null,
                    },
                    percentage: '0',
                };
            }),
        );
    }

    private getCurrentPageIndex(includedPages: Page[], quoteControllerState: QuoteControllerState) {
        return (
            includedPages?.findIndex(
                (page) => page?.route === quoteControllerState?.route?.pageRoute,
            ) ?? 0
        );
    }

    private calculateTrackbarPercentage(
        currentPageIndex: number,
        includedPages: Page[],
        percentage: string,
    ) {
        const length = includedPages?.length ?? 0;

        // Set the min and max percentages for padding
        const minPercentage = 20; // Start at 20%
        const maxPercentage = 80; // Cap at 80%

        // Parse percentage
        const parsed = parseFloat(percentage?.replace('%', '') ?? '');
        const fallbackPercentage =
            !isNaN(parsed) && parsed > 0 ? `${Math.round(parsed)}%` : `${minPercentage}%`;

        // Handle edge cases
        if (length === 0 || currentPageIndex < 0 || currentPageIndex >= length) {
            return fallbackPercentage;
        }

        // If there's only one page, show min percentage
        if (length === 1) {
            return `${minPercentage}%`;
        }

        // Calculate percentage based on currentPageIndex
        // Scale the progress between minPercentage and maxPercentage
        const progress = (currentPageIndex + 1) / length;
        const adjustedPercentage = minPercentage + (maxPercentage - minPercentage) * progress;

        return `${Math.round(adjustedPercentage)}%`;
    }

    private getCurrentTrack(
        tracks: TrackBarData[],
        currentSection: Section,
        quoteFlowType: QuoteFlowType,
    ): TrackBarData | undefined {
        // Sort tracks by specificity (longer routes first)
        const sortedTracks = [...tracks].sort((a, b) => {
            const aSpecificity = a.routes.reduce((acc, route) => acc + route.length, 0);
            const bSpecificity = b.routes.reduce((acc, route) => acc + route.length, 0);
            return bSpecificity - aSpecificity; // More specific routes come first
        });

        // Find the most specific match first
        return sortedTracks.find((el: TrackBarData) => {
            const baseRouteMatch = el.baseRoutes.some((baseRoute) => {
                return currentSection?.baseRoute?.startsWith(baseRoute);
            });

            const routeMatch = el.routes.some((route) => {
                // Only match non-empty routes to avoid false positives with empty strings
                if (route === '' || currentSection?.route === '') {
                    return route === currentSection?.route; // Only match if both are empty
                }

                return (
                    route === currentSection?.route ||
                    currentSection?.route.startsWith(route) ||
                    route.startsWith(currentSection?.route)
                );
            });

            return (
                baseRouteMatch &&
                routeMatch &&
                (el.quoteFlowType == null || el.quoteFlowType == quoteFlowType)
            );
        });
    }

    private getSections(flowSections: Section[], currentTrack: TrackBarData): Section[] {
        const gatherSections = (sections: Section[]): Section[] => {
            return sections.flatMap((section) => {
                const matchBaseRoute = currentTrack?.baseRoutes.some((br) =>
                    section.baseRoute.startsWith(br),
                );

                // Refined route matching to handle nested paths like "home-details/replacement-value"
                const matchRoute = currentTrack?.routes.some((route) => {
                    if (!route && !section.route) return true; // Match if both are empty
                    return (
                        section.route === route || // Exact match
                        section.route.startsWith(route) || // Section path starts with route
                        route.startsWith(section.route) // Route starts with section path
                    );
                });

                let matchedSections: Section[] = matchBaseRoute && matchRoute ? [section] : [];

                // Recursively gather sections from basePageSections if they exist
                if (section.basePageSections && section.basePageSections.length > 0) {
                    matchedSections = matchedSections.concat(
                        gatherSections(section.basePageSections),
                    );
                }

                return matchedSections;
            });
        };

        return gatherSections(flowSections);
    }

    private getPages(currentTrack: TrackBarData, includedSections: Section[]): Page[] {
        if (!currentTrack?.routes) {
            return null;
        }

        const gatherPages = (sections: Section[]): Page[] => {
            let pages: Page[] = [];

            // Loop through currentTrack.routes to preserve the exact order
            // of the routes defined in the track bar data
            currentTrack.routes.forEach((route) => {
                sections.forEach((section) => {
                    if (route.includes('/') && route.startsWith(section.route)) {
                        // Case 1: Route specifies an exact page path (e.g., "home-details/replacement-value")
                        pages = pages.concat(
                            section.pages?.filter(
                                (page) =>
                                    page.route === route.split('/').pop() &&
                                    !page.excludeFromTrackbar,
                            ) || [],
                        );
                    } else if (section.route === route) {
                        // Case 2: Route specifies a broader section (e.g., "home-details")
                        pages = pages.concat(
                            section.pages?.filter((page) => !page.excludeFromTrackbar) || [],
                        );
                        // Recursively gather pages from basePageSections if they exist
                        if (section.basePageSections?.length) {
                            pages = pages.concat(gatherPages(section.basePageSections));
                        }
                    }
                });
            });

            return pages;
        };

        return gatherPages(includedSections);
    }
}
